<template>
    <div>
        <el-row>
            <el-col class="task-sections">
                <div
                    v-for="filter in taskFilters"
                    :key="filter.title"
                    :class="['task-section-title', { active: currentFilter === filter.alias }]"
                    @click="setFilter(filter)"
                >
                    <span>{{ filter.title }}</span>
                    <span v-if="taskCounters[filter.alias] !== null">
                        ({{ taskCounters[filter.alias] }})
                    </span>
                </div>
            </el-col>
        </el-row>
        <el-row class="">
            <el-col class="card-tasks mb10" v-loading="loading">
                <ag-grid-vue
                    class="ag-theme-balham spreadsheet-table-grid"
                    :grid-options="gridOptions"
                    :column-defs="columnDefs"
                />
                <el-row type="flex" align="bottom" class="spreadsheet-bar">
                    <el-col :span="6">
                        <el-pagination
                            background
                            layout="prev, pager, next"
                            :total="pagesInfo.total"
                            :page-size="pageModel.pageSize"
                            :current-page="pageModel.currentPage"
                            :small="true"
                            @current-change="getCurrentPage"
                        />
                    </el-col>
                </el-row>
            </el-col>
        </el-row>
        <el-row>
            <el-col>
                <task-edit-popup
                    :is-visible="isTaskVisible"
                    :task-id="taskId"
                    @changeVisibility="changeVisibility"
                />
            </el-col>
        </el-row>
    </div>
</template>

<script>
import { AgGridVue } from 'ag-grid-vue';
import localReferences from '@/helpers/localReferences';
import '@/assets/styles/list.scss';
import '@/assets/styles/card-blocks.scss';
import { eventBus } from '@/common/eventBus';
import TaskEditPopup from '@/components/task/TaskEditPopup';

export default {
    name: 'Tasks',
    data() {
        return {
            isTaskVisible: false,
            taskId: null,
            gridOptions: {
                rowSelection: 'multiple',
                suppressDragLeaveHidesColumns: false,
                suppressPaginationPanel: true,
                suppressLoadingOverlay: true,
                rowDeselection: true,
                enableCellTextSelection: true,
                headerHeight: 35,
                rowHeight: 35,
                domLayout: 'autoHeight',
                defaultColDef: {
                    sortable: false,
                    filter: false,
                    resizable: true,
                    cellClass: 'column-text pointer'
                },
                onRowDataChanged: this.onRowDataChanged.bind(this),
                onRowSelected: this.onRowSelected.bind(this),
                onRowClicked: this.onRowClicked.bind(this)
            },
            columnDefs: [],
            loading: false,
            pageModel: {
                currentPage: 1,
                pageSize: 4
            },
            totalRows: 0,
            selectedRowsCount: 0,
            currentFilter: 'my',
            taskCounters: {
                my: null,
                check: null,
                work: null,
                finished: null
            },
            taskFilters: []
        };
    },
    components: {
        AgGridVue,
        TaskEditPopup
    },
    props: {
        entityId: {
            type: [String, Number],
            default: 0
        },
        entityType: {
            type: String,
            default: 'deal'
        }
    },
    computed: {
        tasks() {
            return this.$store.state.tasks.tasks;
        },
        pagesInfo() {
            return this.$store.state.tasks.pages;
        },
        filterPreset() {
            return this.taskFilters.filter(item => item.alias === this.currentFilter)[0].filter;
        },
        openedTask() {
            return this.$route.params.openedTask || null;
        }
    },
    created() {
        this.columnDefs = [
            {
                headerName: 'id',
                field: 'id',
                width: 100
            },
            {
                headerName: 'Задача',
                field: 'title',
                autoHeight: true
            },
            {
                headerName: 'Статус',
                field: 'status_id',
                width: 130,
                cellRenderer: ({ data }) => data.status ? data.status.name : ''
            },
            {
                headerName: 'Дата',
                field: 'created_at',
                width: 160
            },
            {
                headerName: 'Постановщик',
                field: 'created_user_id',
                cellRenderer: ({ data }) => data.created_user ? data.created_user.full_name : ''
            },
            {
                headerName: 'Исполнитель',
                field: 'assigned_user_id',
                cellRenderer: ({ data }) => data.assigned_user ? data.assigned_user.full_name : ''
            },
            {
                headerName: 'Срок',
                field: 'planned',
                width: 130
            }
        ];
        this.taskFilters = [
            {
                alias: 'my',
                title: 'мои',
                filter: {
                    assigned_user_id: [this.$store.state.auth.employerId],
                    status_id: [localReferences.taskStatuses.assigned]
                }
            },
            {
                alias: 'check',
                title: 'проверить',
                filter: {
                    created_user_id: [this.$store.state.auth.employerId],
                    status_id: [localReferences.taskStatuses.onCheck]
                }
            },
            {
                alias: 'work',
                title: 'в работе',
                filter: {
                    status_id: [localReferences.taskStatuses.assigned],
                    notEqual: {
                        assigned_user_id: this.$store.state.auth.employerId
                    }
                }
            },
            {
                alias: 'finished',
                title: 'завершены',
                filter: {
                    status_id: [localReferences.taskStatuses.finished]
                }
            }
        ];
    },
    beforeMount() {
        eventBus.$on('tasks-need-update', ({ entityType, entityId }) => {
            this.getFilteredTasks(this.filterPreset, entityType, entityId);
            this.getTasksCounters(entityType, entityId);
        });
        eventBus.$on('show-task', ({ id }) => {
            this.showTask(id);
        });
        eventBus.$on('task-updated', (value) => {
            if (value) {
                this.getTasksCounters(this.entityType, this.entityId);
            }
        });
    },
    mounted() {
        if (this.openedTask !== null) {
            if (this.openedTask.status_id === localReferences.taskStatuses.assigned) {
                if (this.openedTask.assigned_user_id === this.$store.state.auth.employerId) {
                    this.currentFilter = 'my';
                } else {
                    this.currentFilter = 'work';
                }
            }
            if (this.openedTask.status_id === localReferences.taskStatuses.onCheck &&
                this.openedTask.created_user_id === this.$store.state.auth.employerId) {
                this.currentFilter = 'check';
            }
            if (this.openedTask.status_id === localReferences.taskStatuses.finished) {
                this.currentFilter = 'finished';
            }
            this.getFilteredTasks(this.filterPreset, this.openedTask.related_entity_type, this.openedTask.related_entity_id);
            this.getTasksCounters(this.openedTask.related_entity_type, this.openedTask.related_entity_id);
            this.showTask(this.openedTask.id);
        } else if (this.entityId && this.entityId !== 0) {
            this.getFilteredTasks(this.filterPreset, this.entityType, this.entityId);
            this.getTasksCounters(this.entityType, this.entityId);
        }
    },
    destroyed: function() {
        eventBus.$off('tasks-need-update');
    },
    methods: {
        showTask(taskId) {
            this.isTaskVisible = !this.isTaskVisible;
            this.$emit('changeVisibility', this.isTaskVisible);
            this.taskId = taskId;
        },
        changeVisibility() {
            this.getFilteredTasks(this.filterPreset, this.entityType, this.entityId);
            this.$emit('changeVisibility', false);
            this.isTaskVisible = !this.isTaskVisible;
            this.taskId = null;
        },
        async getFilteredTasks(filter, entityType, entityId) {
            if (!entityId) {
                return;
            }
            this.loading = true;
            const params = {
                size: this.pageModel.pageSize,
                page: this.pageModel.currentPage,
                sort: [
                    { colId: 'status_id', sort: 'asc' },
                    { colId: 'created_at', sort: 'desc' }
                ],
                filter: {
                    ...{
                        related_entity_type: entityType,
                        related_entity_id: entityId
                    },
                    ...filter
                }
            };
            await this.$store.dispatch('tasks/getPaginatedList', params);
            this.loading = false;
        },
        async getTasksCounters(entityType, entityId) {
            if (entityId === null || entityId === 'null' || !entityId) {
                this.$notify({ title: 'Ошибка', message: `Не удалось обновить метрики по задачам сделки  ${entityId}`, type: 'error', duration: 60000 });
                return;
            }
            const params = {
                entityType,
                entityId,
                counters: this.taskFilters.map(filter => filter.alias)
            };
            try {
                const response = await this.$store.dispatch('tasks/getTaskRelatedMetric', params);
                this.taskCounters = response.data;
            } catch (e) {
                console.log(e);
            }
        },
        setPageSize(size) {
            this.pageModel.pageSize = size;
            this.getFilteredTasks(this.filterPreset, this.entityType, this.entityId);
        },
        getCurrentPage(page) {
            this.pageModel.currentPage = page;
            this.getFilteredTasks(this.filterPreset, this.entityType, this.entityId);
        },
        onRowDataChanged() {
            this.totalRows = this.gridOptions.api.getDisplayedRowCount();
        },
        onRowSelected() {
            this.selectedRowsCount = this.gridOptions.api.getSelectedRows().length;
        },
        onRowClicked(e) {
            this.showTask(e.node.data.id);
        },
        setFilter(value) {
            //сбрасываем номер открытой страницы при переключении фильтра
            this.pageModel.currentPage = 1;
            this.currentFilter = value.alias;
            this.getFilteredTasks(value.filter, this.entityType, this.entityId);
        }
    },
    watch: {
        tasks(value) {
            if (value) {
                this.gridOptions.api.setRowData(this.tasks);
                if (this.tasks.length) {
                    this.gridOptions.api.sizeColumnsToFit();
                }
            }
        }
    }
};
</script>
